// SuperCash UI kit — small, dependency-free building blocks shared by the merchant
// dashboard and god mode (dark variant via the .sc-theme-god wrapper). Styles live
// in index.css under "SuperCash UI kit".
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// ─────────────────────────── formatting ───────────────────────────

export const inr = (v, { dash = "—" } = {}) =>
  v == null || v === "" ? dash : "₹" + Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const inrCompact = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};

export const when = (iso, withSec = false) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", ...(withSec ? { second: "2-digit" } : {}) })
    : "—";

export const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const shortId = (s, n = 6) => (!s ? "—" : s.length > n * 2 + 1 ? `${s.slice(0, n)}…${s.slice(-4)}` : s);

export async function copyText(text, label = "Copied") {
  try {
    await navigator.clipboard.writeText(String(text));
    toast.success(label);
  } catch {
    toast.error("Couldn't copy — select it manually");
  }
}

// ─────────────────────────── hooks ───────────────────────────

/** Run `fn` now and every `ms` while the tab is visible (and `enabled`). */
export function usePolling(fn, ms, enabled = true) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => { if (document.visibilityState === "visible") ref.current(); };
    const t = setInterval(tick, ms);
    const onVis = () => { if (document.visibilityState === "visible") ref.current(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [ms, enabled]);
}

/** Server-paginated list state: filters + page + loader. `fetcher(params)` -> {items,total,pages,page}. */
export function usePagedList(fetcher, initialFilters = {}, pageSize = 25) {
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const seen = useRef(new Set());
  const [fresh, setFresh] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const res = await fetcher({ ...filters, page, page_size: size });
      // Highlight rows that appeared since the previous load (live updates).
      const ids = res.items.map((i) => i.id);
      const newIds = seen.current.size ? ids.filter((id) => !seen.current.has(id)) : [];
      ids.forEach((id) => seen.current.add(id));
      setFresh(new Set(newIds));
      setData(res);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), page, size]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const reset = () => { setFilters(initialFilters); setPage(1); };
  return { filters, setFilter, setFilters, reset, page, setPage, size, setSize, data, loading, error, load, fresh };
}

/** Push a (debounced) value into a usePagedList filter whenever it changes. */
export function useSyncFilter(setFilter, key, value) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFilter(key, value); }, [key, value]);
}

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ─────────────────────────── layout ───────────────────────────

export function Card({ title, sub, actions, children, pad = true, className = "", style, ...rest }) {
  return (
    <section className={`ui-card ${className}`} style={style} {...rest}>
      {(title || actions) && (
        <header className="ui-card-head">
          <div style={{ minWidth: 0 }}>
            {title && <h3 className="ui-card-title">{title}</h3>}
            {sub && <p className="ui-card-sub">{sub}</p>}
          </div>
          {actions && <div className="ui-row">{actions}</div>}
        </header>
      )}
      {pad ? <div className="ui-card-pad">{children}</div> : children}
    </section>
  );
}

export function Stat({ label, value, foot, accent, testId, onClick }) {
  return (
    <div className={`ui-card ui-stat ${accent ? "is-accent" : ""}`} data-testid={testId} onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
      <span className="ui-eyebrow">{label}</span>
      <span className="ui-stat-value">{value}</span>
      {foot != null && <span className="ui-stat-foot">{foot}</span>}
    </div>
  );
}

export function Tabs({ tabs, value, onChange, testIdPrefix = "tab" }) {
  return (
    <div className="ui-tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} role="tab" aria-selected={value === t.key} data-testid={`${testIdPrefix}-${t.key}`}
          className={`ui-tab ${value === t.key ? "is-active" : ""}`} onClick={() => onChange(t.key)}>
          {t.icon && <span>{t.icon}</span>}
          {t.label}
          {t.count != null && <span className="ui-count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Field({ label, hint, children, style }) {
  return (
    <label className="ui-field" style={style}>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function Switch({ on, onChange, disabled, testId }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} data-testid={testId} disabled={disabled}
      className={`ui-switch ${on ? "is-on" : ""}`} onClick={() => onChange(!on)} />
  );
}

export function Banner({ tone = "warn", children }) {
  return <div className={`ui-banner ${tone === "info" ? "is-info" : tone === "danger" ? "is-danger" : ""}`}>{children}</div>;
}

export function Empty({ icon = "🗂️", title = "Nothing here yet", children }) {
  return (
    <div className="ui-empty">
      <span className="ui-empty-icon">{icon}</span>
      <b>{title}</b>
      {children && <span style={{ fontSize: 13, maxWidth: 420 }}>{children}</span>}
    </div>
  );
}

export function LiveDot({ on = true, label = "LIVE" }) {
  return <span className={`ui-live ${on ? "" : "is-off"}`}>{on ? label : "PAUSED"}</span>;
}

// ─────────────────────────── status badge ───────────────────────────

const TONES = {
  success: "#0f7a45", delivered: "#0f7a45", credit: "#0f7a45", verified: "#0f7a45", active: "#0f7a45",
  pending: "#8a6a00", initiated: "#4931E8", creating: "#4931E8", sending: "#4931E8", retrying: "#b26a00",
  review: "#b26a00", dispute: "#a14a00",
  failed: "#b3261e", error: "#b3261e", dropped: "#6a6478", no_url: "#6a6478", unverified: "#b26a00",
};
const LABELS = { no_url: "no URL", initiated: "awaiting payment" };

export function StatusBadge({ status, label }) {
  const c = TONES[status] || "#6a6478";
  return (
    <span className="ui-badge ui-badge-dot sc-theme-safe" style={{ color: c, background: `${c}14` }}>
      {label || LABELS[status] || status || "—"}
    </span>
  );
}

// ─────────────────────────── data table ───────────────────────────

/**
 * columns: [{ key, label, render(row) -> node, num?, width?, hideLabel? }]
 * Renders a <table>; below 720px the CSS turns every row into a labelled card.
 */
export function DataTable({ columns, rows, onRowClick, activeId, fresh, loading, empty, testId }) {
  if (loading && !rows.length) {
    return (
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="ui-skel" style={{ height: 18, width: `${90 - i * 8}%` }} />)}
      </div>
    );
  }
  if (!rows.length) return empty || <Empty />;
  return (
    <div className="ui-table-wrap sc-scroll" data-testid={testId}>
      <table className="ui-table">
        <thead>
          <tr>{columns.map((c) => <th key={c.key} className={c.num ? "is-num" : ""} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={testId ? `${testId}-row-${r.id}` : undefined}
              className={`${onRowClick ? "is-click" : ""} ${activeId === r.id ? "is-active" : ""} ${fresh?.has(r.id) ? "is-new" : ""}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => (
                <td key={c.key} data-label={c.hideLabel ? "" : c.label} className={c.num ? "is-num" : ""}>
                  {c.render ? c.render(r) : r[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ page, pages, total, size, onPage, onSize, sizes = [10, 25, 50, 100] }) {
  const nums = [];
  const lo = Math.max(1, page - 2);
  const hi = Math.min(pages, page + 2);
  if (lo > 1) nums.push(1, lo > 2 ? "…" : null);
  for (let i = lo; i <= hi; i += 1) nums.push(i);
  if (hi < pages) nums.push(hi < pages - 1 ? "…" : null, pages);
  const from = total ? (page - 1) * size + 1 : 0;
  const to = Math.min(total, page * size);
  return (
    <div className="ui-pager">
      <span className="ui-mono ui-muted" style={{ fontSize: 12 }}>
        {from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")} of <b>{total.toLocaleString("en-IN")}</b>
      </span>
      <div className="ui-pager-pages">
        <button className="ui-page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">←</button>
        {nums.filter((n) => n !== null).map((n, i) =>
          n === "…" ? <span key={`e${i}`} className="ui-faint" style={{ padding: "0 4px" }}>…</span> : (
            <button key={n} className={`ui-page-btn ${n === page ? "is-current" : ""}`} onClick={() => onPage(n)}>{n}</button>
          ))}
        <button className="ui-page-btn" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">→</button>
        {onSize && (
          <select className="ui-select" value={size} onChange={(e) => onSize(Number(e.target.value))} style={{ minHeight: 32, marginLeft: 6 }}>
            {sizes.map((s) => <option key={s} value={s}>{s} / page</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── overlays ───────────────────────────

function useEsc(onClose) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", h); document.body.style.overflow = prev; };
  }, [onClose]);
}

export function Drawer({ title, sub, onClose, children, actions, theme = "" }) {
  useEsc(onClose);
  return (
    <div className={theme}>
      <div className="ui-overlay" onClick={onClose} />
      <aside className="ui-drawer" role="dialog" aria-modal="true">
        <header className="ui-drawer-head">
          <div style={{ minWidth: 0 }}>
            <h3 className="ui-card-title" style={{ fontSize: 19 }}>{title}</h3>
            {sub && <div className="ui-card-sub">{sub}</div>}
          </div>
          <div className="ui-row" style={{ flexWrap: "nowrap" }}>
            {actions}
            <button className="ui-x" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </header>
        <div className="ui-drawer-body">{children}</div>
      </aside>
    </div>
  );
}

export function Modal({ title, onClose, children, footer, theme = "", wide = false }) {
  useEsc(onClose);
  return (
    <div className={theme}>
      <div className="ui-overlay" onClick={onClose} />
      <div className={`ui-modal${wide ? " is-wide" : ""}`} role="dialog" aria-modal="true">
        <header className="ui-modal-head">
          <h3 className="ui-card-title">{title}</h3>
          <button className="ui-x" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="ui-modal-body">{children}</div>
        {footer && <footer className="ui-modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

// ─────────────────────────── detail bits ───────────────────────────

export function KV({ items }) {
  return (
    <div className="ui-kv">
      {items.filter(Boolean).map(([k, v, opts = {}]) => (
        <div key={k} className="ui-kv-item">
          <span className="ui-eyebrow">{k}</span>
          <span className="ui-kv-val">
            {v == null || v === "" ? <span className="ui-faint">—</span> : v}
            {opts.copy && v ? <button className="ui-link" style={{ marginLeft: 8, fontSize: 11 }} onClick={() => copyText(opts.copy === true ? v : opts.copy)}>copy</button> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Timeline({ events }) {
  if (!events?.length) return <div className="ui-faint" style={{ fontSize: 13 }}>No events yet.</div>;
  return (
    <div className="ui-timeline">
      {events.map((e, i) => (
        <div key={i} className="ui-tl-item">
          <div className="ui-tl-msg">{e.message}</div>
          <div className="ui-tl-at">{when(e.at, true)} · {ago(e.at)}</div>
        </div>
      ))}
    </div>
  );
}

export function Json({ value }) {
  return <pre className="ui-json">{JSON.stringify(value, null, 2)}</pre>;
}

export function CopyField({ label, value, secret }) {
  const [show, setShow] = useState(!secret);
  const shown = show ? value : value ? "•".repeat(Math.min(24, value.length - 4)) + value.slice(-4) : "";
  return (
    <Field label={label}>
      <div className="ui-row" style={{ flexWrap: "nowrap" }}>
        <input readOnly className="ui-input is-mono" value={shown || ""} placeholder="—" onFocus={(e) => e.target.select()} />
        {secret && <button type="button" className="ui-btn ui-btn-sm" onClick={() => setShow((s) => !s)}>{show ? "Hide" : "Show"}</button>}
        <button type="button" className="ui-btn ui-btn-sm" disabled={!value} onClick={() => copyText(value)}>Copy</button>
      </div>
    </Field>
  );
}
